/*
 * Copyright (c) 2020 NOOGA DEVELOPMENT, All rights reserved.
 * Author Raman Kremko <raman.kremko@noogadev.com>
 */

import {
    InspectorHomeActionType,
    InspectorHomeAction,
    SSEOpen,
    SSEClosed,
    SSEError,
    SSEOnMessage,
    SSEConnecting,
} from '../actions/inspectorHomeActions';
import { AnyAction, Dispatch, MiddlewareAPI } from 'redux';
import { bindActionCreators } from 'redux';
import { EventSourcePolyfill, OnMessageEvent } from 'ng-event-source';
import { MainStatisticsDto } from '../api/mainStatistics/mainStatisticsClient.typings';
import { logoutUser } from '../actions/userActions';
import { mainStatisticsClient } from '../api/mainStatistics/mainStatisticsClient';
import { interval, Subscription } from 'rxjs';
import { mapTo, tap } from 'rxjs/operators';
import { API_ENDPOINT } from '../api/apiClient';

export type SSEErrorType = {
    errorMessage: string;
    errorCode: number;
};

const eventHandlers = {
    onopen: SSEOpen,
    onclose: SSEClosed,
    onerror: SSEError,
    onmessage: SSEOnMessage,
    onconnection: SSEConnecting,
};

const SSE_URL: string = `${API_ENDPOINT}/1.0.0/dashboard/counters`;

const SSEOpenPredicate = (action: InspectorHomeAction) => action.type === InspectorHomeActionType.SSE_CONNECTING;
const SSEClosePredicate = (action: InspectorHomeAction) => action.type === InspectorHomeActionType.SSE_CLOSED;

let SSE: EventSourcePolyfill;
let mockSSE: Subscription;

const SSEStart = (store: MiddlewareAPI) => {
    const boundEventHandlers = bindActionCreators(eventHandlers, store.dispatch);

    if (import.meta.env.VITE_MOCK_API) {
        boundEventHandlers.onopen();

        mockSSE = interval(1000)
            .pipe(
                tap(() => {
                    mainStatisticsClient.getMainStatistics().then((data) => boundEventHandlers.onmessage(data));
                }),
                mapTo(null),
            )
            .subscribe();
    } else {
        SSE = new EventSourcePolyfill(SSE_URL, {});

        SSE.onopen = () => {
            boundEventHandlers.onopen();
        };

        SSE.onmessage = (message: OnMessageEvent) => {
            const parseMessage: MainStatisticsDto = JSON.parse(message.data);
            boundEventHandlers.onmessage(parseMessage);
        };

        SSE.onerror = (error: SSEErrorType) => {
            if (SSE.readyState === SSE.CONNECTING) {
                console.log('SSE reconnecting...');
            } else if (error.errorCode === 401) {
                if (import.meta.env.VITE_MOCK_API) {
                    return;
                }

                store.dispatch(logoutUser());
            } else {
                boundEventHandlers.onerror(error);
            }
        };
    }
};

export const createSSEMiddleware = () => (store: MiddlewareAPI) => (next: Dispatch) => (action: AnyAction) => {
    if (SSEOpenPredicate(action)) {
        SSEStart(store);
        return next(action);
    }

    if (SSEClosePredicate(action)) {
        if (import.meta.env.VITE_MOCK_API) {
            mockSSE.unsubscribe();
        } else {
            SSE.close();
        }

        return next(action);
    }

    return next(action);
};
